import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideCompare,
  GuideBlockList,
} from "../GuideBlocks.js";

const ReflectionBeforeActionSection = ({ heading, accentIndex = 3, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="reflection-before-action"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      The gap between something happening and you responding to it is small — often just seconds.
      But that gap is where every good decision and every regretted one gets decided.
    </GuideLead>
    <p className="guide-panel__p">
      <strong>Reflection before action</strong> is not about slowing your whole life down. It is
      about protecting that one small gap.
    </p>
    <GuideSectionLabel>Why the pause gets skipped</GuideSectionLabel>
    <p className="guide-panel__p">
      Reacting feels productive. It feels like momentum, like control. Pausing, by contrast, can
      feel like hesitation — especially if you associate speed with competence.
    </p>
    <p className="guide-panel__p">
      But most regretted decisions were not made too slowly. They were made in the gap where Event,
      Interpretation, and Evidence were still tangled together, before any of them had been looked
      at clearly.
    </p>
    <GuideSectionLabel>Where the pause belongs in EIER</GuideSectionLabel>
    <p className="guide-panel__p">
      In the EIER framework — <strong>Event → Interpretation → Evidence → Response</strong> — the
      pause belongs between Evidence and Response. Not before you notice pressure (you do not get
      to choose that), but before you act on an unexamined read.
    </p>
    <GuideCompare
      leftTitle="Reaction"
      left={
        <p>
          Skips straight from a fast Interpretation to Response — without checking Evidence.
        </p>
      }
      rightTitle="Response"
      right={
        <p>
          Lets Interpretation and Evidence be seen clearly first, then chooses the next move
          deliberately.
        </p>
      }
    />
    <GuideSectionLabel>Practicing the pause</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="energy"
        title="Use a physical cue"
        description="A breath, a walk to get water, closing your laptop for sixty seconds — anything that interrupts the automatic jump to Response."
      />
      <GuideCard
        iconKey="thinking"
        title="Ask one question before you act"
        description='"What would I be missing if my current Interpretation is wrong?"'
      />
      <GuideCard
        iconKey="communication"
        title="Write before you send"
        description="For emails, messages, or feedback — draft it, then wait even five minutes. You are giving Interpretation a chance to settle."
      />
      <GuideCard
        iconKey="growth"
        title="Reflect after, not just before"
        description="Briefly reviewing Event, Interpretation, Evidence, and Response builds the pattern recognition that makes the pause faster over time."
      />
    </GuideCardGrid>
    <GuideBlockList
      items={[
        "The pause does not have to be long. It just has to exist.",
        "That is usually the only difference between a reaction you regret and a response you stand behind.",
      ]}
    />
    <GuideCallout variant="amber">
      Protect the gap between what happened and what you do next — that is where better decisions
      are made.
    </GuideCallout>
  </GuidePanel>
);

export default ReflectionBeforeActionSection;
